import { useCallback } from "react";
import { Button } from "@components/ui/button";
import { TextShimmer } from "@components/ui/text-shimmer";
import { ArrowRight, Paperclip, Sparkles, Square } from "lucide-react";
import { useAdsChatActions } from "@components/ads/hooks";
import { Chat, useChatIsStreaming } from "@launch10/use-langgraph-stream";
import { CreditGate } from "@components/shared/chat/input/CreditGate";
import { useWorkflow, selectSubstep } from "@context/WorkflowProvider";
import { Ads } from "@shared";

/**
 * Ads chat input using Chat compound components from use-langgraph-stream.
 *
 * Uses context-aware components that automatically bind to the composer,
 * handle streaming state, and manage attachments. Submit behavior is
 * configured at Chat.Root level.
 */
export default function AdsChatInput() {
  const { submit } = useAdsChatActions();
  const isStreaming = useChatIsStreaming();
  const substep = useWorkflow(selectSubstep);

  // Known gap: this cross-form button does NOT await pending per-form saves
  // before submitting. If a user toggles a lock and clicks Refresh All within
  // the debounce/PATCH window, the in-flight PATCH may not land before the
  // graph's loadAssets reads Rails for this turn — that turn's generation
  // will ignore the lock. Next turn catches up because loadAssets now runs
  // on every turn and Rails is authoritative.
  //
  // The complete fix (2B) is to add a `flush` method to the form registry
  // alongside `cancel`, have useAssetForm / StructuredSnippetsForm register a
  // flush that awaits saveNow on pending data, and await formRegistry.flushAll()
  // here before submit. Deferred — the lock refactor was already large, the
  // race is one-turn and self-healing, and per-form refresh buttons in
  // useAssetForm already flush their own form via `await saveNow`.
  const onRefreshSuggestions = useCallback(() => {
    if (!submit) return;
    const stage = substep as Ads.StageName;
    const assets = Ads.refreshAllCommand(stage);
    submit({
      intent: {
        type: "refresh_assets" as const,
        payload: { stage, assets },
        createdAt: new Date().toISOString(),
      },
    });
  }, [substep, submit]);

  return (
    <div className="flex flex-col gap-1 w-full">
      <CreditGate>
        <Chat.Input.DropZone
          className="relative bg-white border border-neutral-300 rounded-2xl p-3 flex flex-col min-h-[80px]"
          data-testid="ads-chat-dropzone"
        >
          <Chat.Input.AttachmentList className="flex flex-wrap gap-2 mb-2" />

          <Chat.Input.Textarea
            placeholder="Ask Houston anything, or drag an image in…"
            className="flex-1 text-xs min-h-[40px]"
            data-testid="ads-chat-input"
          />

          <div className="flex items-center justify-between mt-auto pt-2">
            <Chat.Input.FileButton className="text-base-500 p-1 hover:bg-neutral-100 rounded">
              <Paperclip className="size-4" />
            </Chat.Input.FileButton>

            <Chat.Input.SubmitButton
              stopIcon={<Square className="size-3" fill="currentColor" />}
              className="rounded-xl bg-primary-500 text-white hover:bg-primary-600 size-9 flex items-center justify-center"
              data-testid="ads-chat-submit"
            >
              <ArrowRight className="size-4" />
            </Chat.Input.SubmitButton>
          </div>
        </Chat.Input.DropZone>
      </CreditGate>

      <Button
        variant="link"
        className="text-base-400 font-normal text-xs px-0 py-1 h-auto justify-start gap-1 no-underline hover:no-underline"
        onClick={onRefreshSuggestions}
        data-testid="refresh-all-suggestions"
      >
        <Sparkles className="size-3.5" />{" "}
        {isStreaming ? (
          <TextShimmer>Refresh All Suggestions</TextShimmer>
        ) : (
          "Refresh All Suggestions"
        )}
      </Button>
    </div>
  );
}
